import { useMemo, useRef } from 'react';

import dayjs from 'dayjs';

import { QuickQuoteConfig } from 'contexts/serviceContext';
import { isAutoTransport } from 'lib/autoTransport/isAutoTransport';
import { Email } from 'types/Email';
import { Stop, TransportType } from 'types/QuoteRequest';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionCategories,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import {
  QuoteChanges,
  type SuggestedQuoteChange,
} from 'types/suggestions/QuoteSuggestions';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';
import { normalizeLtlFreightClassSuggestion } from 'utils/densityToFreightClass';
import { resolveTransportType } from 'utils/loadInfoAndBuilding';

import { sanitizeAutoTransportDetails } from '../FTL/autoTransportVehicleHelpers';
import {
  EMPTY_STOP,
  KencoUrgency,
  QuickQuoteInputs,
  QuoteCountries,
  QuoteFormStop,
  QuoteMode,
} from '../types';
import { parseLocation } from '../utils/location';
import {
  getSuggestedCommodities,
  mapSuggestedAccessorialsToLTLSelected,
  mapSuggestedCommoditiesToLTLItems,
  toPositiveNumber,
} from '../utils/mapSuggestedCommoditiesToLTLItems';
import { normalizePortalLoadIdToNumericCore } from '../utils/normalizePortalLoadId';
import { portalLoadIdCandidateFromQuotingPortalSuggestion } from '../utils/portalSuggestionLoadId';
import { resolveQuickQuoteDates } from '../utils/resolveQuickQuoteDates';
import {
  hasTaiLtlPickupHours,
  resolveTaiLtlPickupWindow,
} from '../utils/resolveTaiLtlPickupWindow';
import { useHasSuperDispatchQuoting } from './useHasSuperDispatchQuoting';
import { useSuperDispatchVehicleCatalog } from './useSuperDispatchVehicleCatalog';

function getStopLocationLabel(stop: Stop): string {
  const city = stop.address?.city?.trim() ?? '';
  const state = stop.address?.state?.trim() ?? '';
  const zip = stop.address?.zip?.trim() ?? '';
  const addressLine1 = stop.address?.addressLine1?.trim() ?? '';
  const addressLine2 = stop.address?.addressLine2?.trim() ?? '';
  const street = [addressLine1, addressLine2].filter(Boolean).join(', ');
  const cityState = [city, state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, zip].filter(Boolean).join(' ');

  if (street && cityStateZip) {
    return `${street}, ${cityStateZip}`.trim();
  }

  if (cityStateZip) {
    return cityStateZip;
  }

  if (street) {
    return street;
  }

  return zip || city || state || '';
}

function getStopZipOrCityStateLabel(stop: Stop): string {
  const city = stop.address?.city?.trim() ?? '';
  const state = stop.address?.state?.trim() ?? '';
  const zip = stop.address?.zip?.trim() ?? '';

  if (zip) {
    return zip;
  }

  if (city && state) {
    return `${city}, ${state}`;
  }

  return '';
}

function formatParsedLocationLabel(
  location: Maybe<{ city?: string; state?: string; zip?: string }>
): string {
  if (!location) {
    return '';
  }

  if (location.zip) {
    return location.zip;
  }

  return `${location.city}, ${location.state}`;
}

function mapSuggestedStopToFormStop(
  stop: Stop,
  index: number,
  keepFullSuggestionLocation: boolean
): QuoteFormStop {
  const externalTMSID = stop.externalTMSID?.trim() ?? '';
  return {
    order: index,
    location: keepFullSuggestionLocation
      ? getStopLocationLabel(stop)
      : getStopZipOrCityStateLabel(stop),
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zip: stop.address?.zip ?? '',
    country: stop.address?.country as QuoteCountries,
    addressName: stop.address?.name ?? '',
    addressLine1: keepFullSuggestionLocation
      ? (stop.address?.addressLine1 ?? '')
      : '',
    addressLine2: keepFullSuggestionLocation
      ? (stop.address?.addressLine2 ?? '')
      : '',
    // Seeds Tai Address book (RHFLocationSelect on stops.N.externalTMSID).
    ...(externalTMSID ? { externalTMSID } : {}),
  };
}

/**
 * Hook to generate default form values from suggestions and config
 * This replaces a 220+ line memoized calculation in the main component
 */
export const useQuickQuoteDefaultValues = (
  clickedSuggestion: Maybe<GenericSuggestion>,
  quickQuoteConfig: Maybe<QuickQuoteConfig>,
  isMultiStopQuickQuoteEnabled: boolean,
  isTurvoActive: boolean,
  isActiveTaiLtlIntegration: boolean,
  isThreeGActive: boolean,
  email: Maybe<Email>,
  normalizeLoadId = normalizePortalLoadIdToNumericCore,
  preserveInvalidPortalLoadId = true
): QuickQuoteInputs => {
  const suggestedFields = clickedSuggestion?.suggested as Undef<QuoteChanges>;
  const hasSuperDispatchQuoting = useHasSuperDispatchQuoting();
  // Service features load async in live/harness; do not rebuild defaults when SD
  // connects or the Quick Quote form reset() wipes in-progress vehicle entry.
  const hasSuperDispatchQuotingRef = useRef(hasSuperDispatchQuoting);
  hasSuperDispatchQuotingRef.current = hasSuperDispatchQuoting;
  const shouldLoadVehicleCatalog =
    hasSuperDispatchQuoting &&
    (isAutoTransport(suggestedFields?.transportType) ||
      Boolean(suggestedFields?.autoTransport));
  // Prefetch only: readiness stays out of the defaults memo so the form isn't reset on load.
  useSuperDispatchVehicleCatalog(shouldLoadVehicleCatalog);

  return useMemo<QuickQuoteInputs>(() => {
    // TODO: this also needs to be gated via usergroup level feature flags.
    const mode =
      suggestedFields?.mode?.toUpperCase() === 'LTL'
        ? QuoteMode.LTL
        : QuoteMode.TL;
    const keepFullSuggestionLocation =
      mode !== QuoteMode.LTL || isTurvoActive || isActiveTaiLtlIntegration;

    // Somewhere along the way the array of stops returned by API
    // is getting converted to an object with numeric keys (likely by React state serialization)
    // so we enforce it as an array so AI-filling works
    const suggestedStops = asArrayOrObjectValues(
      suggestedFields?.stops as Undef<Stop[] | Record<string, Stop>>
    );
    const hasMultipleStops = suggestedStops.length > 2;

    const firstStop = suggestedStops[0];
    const hasValidReadyTime =
      hasMultipleStops &&
      firstStop?.stopType === 'pickup' &&
      firstStop?.readyTime &&
      firstStop.readyTime !== undefined;

    let pickupDate: Maybe<Date> = null;
    if (hasValidReadyTime) {
      pickupDate = new Date(firstStop.readyTime!);
    } else if (suggestedFields?.pickupDate) {
      pickupDate = new Date(suggestedFields.pickupDate);
    }

    const hasValidMustDeliver =
      hasMultipleStops &&
      firstStop?.stopType === 'dropoff' &&
      firstStop?.mustDeliver &&
      firstStop.mustDeliver !== undefined;

    let deliveryDate: Maybe<Date> = null;
    if (hasValidMustDeliver) {
      deliveryDate = new Date(firstStop.mustDeliver!);
    } else if (suggestedFields?.deliveryDate) {
      deliveryDate = new Date(suggestedFields.deliveryDate);
    }

    let stops: QuoteFormStop[];

    if (hasMultipleStops && isMultiStopQuickQuoteEnabled) {
      stops = suggestedStops.map((stop, index) =>
        mapSuggestedStopToFormStop(stop, index, keepFullSuggestionLocation)
      );
    } else if (suggestedStops.length > 0) {
      const pickupStop =
        suggestedStops.find((stop) => stop.stopType === 'pickup') ??
        suggestedStops[0];
      const deliveryStop =
        [...suggestedStops]
          .reverse()
          .find((stop) => stop.stopType === 'dropoff') ??
        suggestedStops[suggestedStops.length - 1];

      stops = [
        mapSuggestedStopToFormStop(pickupStop, 0, keepFullSuggestionLocation),
        mapSuggestedStopToFormStop(deliveryStop, 1, keepFullSuggestionLocation),
      ];
    } else if (suggestedFields) {
      // Process suggested pickup location
      const pickupLocation = parseLocation(
        suggestedFields.pickupZip
          ? suggestedFields.pickupZip
          : `${suggestedFields.pickupCity}, ${suggestedFields.pickupState}`
      );

      const deliveryLocation = parseLocation(
        suggestedFields?.deliveryZip
          ? suggestedFields.deliveryZip
          : `${suggestedFields?.deliveryCity}, ${suggestedFields?.deliveryState}`
      );

      stops = [
        {
          order: 0,
          location: formatParsedLocationLabel(pickupLocation),
          city: pickupLocation?.city ?? '',
          state: pickupLocation?.state ?? '',
          zip: pickupLocation?.zip ?? '',
          country:
            (pickupLocation?.country as QuoteCountries) ?? QuoteCountries.USA,
        },
        {
          order: 1,
          location: formatParsedLocationLabel(deliveryLocation),
          city: deliveryLocation?.city ?? '',
          state: deliveryLocation?.state ?? '',
          zip: deliveryLocation?.zip ?? '',
          country:
            (deliveryLocation?.country as QuoteCountries) ?? QuoteCountries.USA,
        },
      ];
    } else {
      // Else initialize empty stops
      stops = [EMPTY_STOP, EMPTY_STOP];
    }

    // Suggestion payloads mix casing ("AUTO" vs "auto"); resolve against the enum.
    // Auto is only offered with Super Dispatch, so ignore it for other tenants.
    const resolveAvailableTransportType = (value: Maybe<string>) => {
      const resolved = resolveTransportType(value);
      if (
        resolved === TransportType.AUTO &&
        !hasSuperDispatchQuotingRef.current
      ) {
        return null;
      }

      return resolved;
    };
    const transportType =
      resolveAvailableTransportType(suggestedFields?.transportType ?? null) ??
      resolveAvailableTransportType(
        quickQuoteConfig?.defaultTransportType ?? null
      ) ??
      TransportType.VAN;

    const ltlData = (() => {
      if (mode !== QuoteMode.LTL) {
        return {
          commodity: {
            freightClass: undefined,
            nmfcCode: '',
            totalWeight: undefined,
            stackable: undefined,
            sideLoadEnabled: false,
            allowSplitMerge: false,
            hazmat: undefined,
          },
          items: [],
          selectedAccessorials: [],
          pickupServices: [],
          deliveryServices: [],
          compatibilityProfiles: [],
          value: {
            declaredValue: undefined,
            deductibleValue: undefined,
            shipValue: undefined,
          },
        };
      }

      const suggestedCommodities = getSuggestedCommodities(suggestedFields);
      const shouldNormalizeFreightClassSuggestions =
        isActiveTaiLtlIntegration || isThreeGActive || isTurvoActive;

      const items = mapSuggestedCommoditiesToLTLItems(suggestedCommodities, {
        shouldNormalizeFreightClass: shouldNormalizeFreightClassSuggestions,
        isThreeGActive,
      });

      const firstCommodity = suggestedCommodities[0];

      const selectedAccessorials =
        mapSuggestedAccessorialsToLTLSelected(suggestedFields);

      return {
        commodity: {
          freightClass: shouldNormalizeFreightClassSuggestions
            ? normalizeLtlFreightClassSuggestion(firstCommodity?.freightClass)
            : firstCommodity?.freightClass,
          nmfcCode: firstCommodity?.nmfc ?? '',
          sideLoadEnabled: false,
          allowSplitMerge: false,
        },
        items,
        selectedAccessorials,
        pickupServices: [],
        deliveryServices: [],
        compatibilityProfiles: [],
      };
    })();

    // Default submit-bid / portal load id for the quick-quote form.
    // Prefer the email thread's bidding portal reference when present.
    // Otherwise use sourceExternalID from the active suggestion only when it is a
    // Quick Quote suggestion originating from the quoting portal.
    const portalLoadRef = email?.biddingPortalLoadReference?.trim() ?? '';
    const quickQuoteSuggestion =
      clickedSuggestion?.pipeline === SuggestionPipelines.QuickQuote &&
      clickedSuggestion?.category === SuggestionCategories.QuickQuote
        ? (clickedSuggestion as SuggestedQuoteChange)
        : undefined;
    const suggestionPortalLoadRef =
      portalLoadIdCandidateFromQuotingPortalSuggestion(quickQuoteSuggestion);

    const rawLoadIdDefault = portalLoadRef || suggestionPortalLoadRef;
    const normalizedLoadId = normalizeLoadId(rawLoadIdDefault);
    const loadIdDefault =
      normalizedLoadId || (preserveInvalidPortalLoadId ? rawLoadIdDefault : '');

    const parsedValues: QuickQuoteInputs = {
      customerName: suggestedFields?.customerExternalTMSID ?? '',
      quoteContactName: '',
      transportType: transportType,
      pickupDate: pickupDate as Date,
      pickupCloseDate: null,
      deliveryDate: deliveryDate as Date,
      deliveryCloseDate: null,
      ...(isThreeGActive
        ? { quoteExpirationDate: dayjs().add(7, 'days').toDate() }
        : {}),
      stops: stops,
      isSubmitToTMS: false,
      ltl: ltlData,

      // Integration-specific fields
      kencoUrgency: KencoUrgency.CanGet,
      autoTransport: sanitizeAutoTransportDetails(
        suggestedFields?.autoTransport
      ),
      customAccessorials: [],
      loadId: loadIdDefault,
    };

    // Tai LTL page: pickup ready + close are required. Seed them from the service's pickup
    // hours, or ready=now and close=ready+4h, when the suggestion is already LTL (initial
    // defaults/reset).
    // FTL→LTL switches are handled in QuickQuoteForm via quoteMode.
    const isTaiLtlDefaults =
      isActiveTaiLtlIntegration && mode === QuoteMode.LTL;

    if (isTaiLtlDefaults) {
      const { pickupDate: resolvedPickup, pickupCloseDate } =
        resolveTaiLtlPickupWindow(
          pickupDate,
          null,
          undefined,
          quickQuoteConfig
        );

      parsedValues.pickupDate = resolvedPickup;
      parsedValues.pickupCloseDate = pickupCloseDate;
      parsedValues.deliveryCloseDate = null;
    }

    // If dates are required, resolve pickup and derive delivery when missing.
    if (quickQuoteConfig?.isFTLQuickQuoteDatesRequired) {
      // Tai pickup hours already set Ready; the FTL default is the rep's local start of day,
      // which is not a Tai wall-clock date.
      const keepsTaiPickupHours =
        isTaiLtlDefaults && hasTaiLtlPickupHours(quickQuoteConfig);
      const {
        pickupDate: resolvedPickupDate,
        deliveryDate: resolvedDeliveryDate,
      } = resolveQuickQuoteDates(
        keepsTaiPickupHours ? parsedValues.pickupDate : pickupDate,
        deliveryDate
      );

      parsedValues.pickupDate = resolvedPickupDate;
      parsedValues.deliveryDate = resolvedDeliveryDate;

      const isMissingSuggestedDeliveryDate =
        !deliveryDate || Number.isNaN(deliveryDate.getTime());

      // If suggestion does not have a delivery date, use estimated KMA transit days to calculate it.
      if (isMissingSuggestedDeliveryDate) {
        const estimatedKMATransitDays = toPositiveNumber(
          suggestedFields?.estimatedKMATransitDays
        );

        if (estimatedKMATransitDays) {
          parsedValues.deliveryDate = dayjs(resolvedPickupDate)
            .add(estimatedKMATransitDays, 'day')
            .toDate();
        }
      }

      // FTL defaults may replace Ready; keep Tai Close aligned with it.
      if (isTaiLtlDefaults) {
        const { pickupCloseDate } = resolveTaiLtlPickupWindow(
          parsedValues.pickupDate,
          null,
          undefined,
          quickQuoteConfig
        );

        parsedValues.pickupCloseDate = pickupCloseDate;
      }
    }

    return parsedValues;
  }, [
    clickedSuggestion,
    suggestedFields,
    quickQuoteConfig,
    isMultiStopQuickQuoteEnabled,
    isTurvoActive,
    isActiveTaiLtlIntegration,
    isThreeGActive,
    email,
    normalizeLoadId,
    preserveInvalidPortalLoadId,
  ]);
};
